import { Link, useNavigate } from "react-router-dom";
import { Linkedin, Music2, Twitter, Youtube } from "lucide-react";

const LINKS = [
  { label: "Framework", target: "framework" },
  { label: "Services", target: "services" },
  { label: "Signal Library", target: "signal-library" },
  { label: "Executive Briefings", target: "services" },
  { label: "Contact", target: "contact" },
];

// Swap these `#` hrefs with the real URLs when ready.
const SOCIALS = [
  { label: "LinkedIn", href: "#", icon: Linkedin, testid: "social-linkedin" },
  { label: "TikTok", href: "#", icon: Music2, testid: "social-tiktok" },
  { label: "X", href: "#", icon: Twitter, testid: "social-x" },
  { label: "YouTube", href: "#", icon: Youtube, testid: "social-youtube" },
];

export default function Footer() {
  const navigate = useNavigate();

  const handleClick = (target) => {
    if (target === "contact") {
      navigate("/contact");
      return;
    }
    if (window.location.pathname !== "/") {
      navigate(`/#${target}`);
      setTimeout(() => {
        document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
      }, 50);
    } else {
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/5 bg-axiom-surface/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="signal-dot" style={{ width: 6, height: 6 }} />
              <span className="font-display text-xl font-bold tracking-tight text-axiom-text">
                AXIOM-AI
              </span>
            </Link>
            <h3 className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl text-axiom-text font-bold leading-tight max-w-2xl">
              Axiom-AI turns operational complexity into executive clarity.
            </h3>
            <p className="mt-4 text-sm text-axiom-dim max-w-md">
              Operator-grade supply-chain intelligence and consulting.
            </p>
          </div>

          <div className="lg:col-span-3">
            <div className="num-label mb-4">NAVIGATE</div>
            <ul className="space-y-3">
              {LINKS.map((l) => (
                <li key={l.label}>
                  <button
                    data-testid={`footer-link-${l.target}`}
                    onClick={() => handleClick(l.target)}
                    className="text-sm text-axiom-muted hover:text-axiom-text transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="num-label mb-4">SIGNAL FEED</div>
            <p className="text-sm text-axiom-muted leading-relaxed">
              Subscribe to the Signal Library — field intelligence for
              operators, executives, and investors.
            </p>
            <button
              data-testid="footer-cta"
              onClick={() => navigate("/contact")}
              className="btn-axiom-outline text-xs mt-4"
            >
              Request a briefing
            </button>

            <div className="mt-8">
              <div className="num-label mb-3">FOLLOW THE SIGNAL</div>
              <div className="flex items-center gap-2" data-testid="footer-socials">
                {SOCIALS.map((s) => {
                  const Icon = s.icon;
                  const disabled = s.href === "#";
                  return (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={s.testid}
                      aria-label={s.label}
                      aria-disabled={disabled ? "true" : "false"}
                      onClick={(e) => disabled && e.preventDefault()}
                      className={`w-9 h-9 inline-flex items-center justify-center border border-white/10 transition-colors ${
                        disabled
                          ? "text-axiom-dim cursor-not-allowed"
                          : "text-axiom-muted hover:text-axiom-accent hover:border-axiom-accent/40"
                      }`}
                      title={disabled ? `${s.label} (link pending)` : s.label}
                    >
                      <Icon size={14} strokeWidth={1.6} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row gap-3 md:items-center justify-between font-mono text-xs text-axiom-dim">
          <span>© {new Date().getFullYear()} AXIOM-AI INTELLIGENCE / ALL RIGHTS RESERVED</span>
          <div className="flex items-center gap-2">
            <span className="signal-dot" style={{ width: 6, height: 6 }} />
            <span>OPERATOR-GRADE / SIGNAL.LIVE</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
